import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useOrgMembers } from '@/hooks/useOrgMembers';
import { apiGet } from '@/lib/http/client';
import { logger } from '@/lib/logger';

/** A selectable org member for owner / visibility pickers. */
export interface OrgUserOption {
  userId: number;
  entityId: string;
  displayName: string;
  email: string;
}

/** Shape of one entry in the existing `team_users_list` response (a bare array). */
interface TeamUser {
  id: number;
  entity_id: string;
  display_name: string;
  email: string;
}

const EMPTY_USERS: readonly OrgUserOption[] = Object.freeze([]);
const STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Fetches every member of an org (the full set, not a page) for owner /
 * visibility selection. Backed by the existing `GET /api/orgs/{orgId}/users/`
 * (`team_users_list`), which returns a bare array of org members with their
 * CRM entity id and display name. Disabled until `orgId` is known.
 *
 * Viewers are excluded from the returned list: `team_users_list` doesn't
 * return `memberKind`, so membership data is cross-referenced from
 * `useOrgMembers` (keyed by `user.id`) to filter them out. If that auxiliary
 * fetch fails, the unfiltered user list is returned rather than surfacing a
 * fatal error on a picker that previously worked fine without this data.
 *
 * @returns `{ users, isLoading, isError }` — a stable object reference that
 *   changes only when query state changes.
 */
export function useOrgUsers(orgId: number | null | undefined) {
  const result = useQuery({
    queryKey: ['orgUsers', orgId] as const,
    queryFn: async (): Promise<OrgUserOption[]> => {
      try {
        const data = await apiGet<TeamUser[]>(`/api/orgs/${orgId}/users/`);
        return (data ?? []).map(user => ({
          userId: user.id,
          entityId: user.entity_id,
          displayName: user.display_name,
          email: user.email,
        }));
      } catch (error) {
        logger.error('Failed to fetch org users', error as Error, {
          component: 'useOrgUsers',
          orgId,
        });
        throw error;
      }
    },
    staleTime: STALE_TIME_MS,
    enabled: !!orgId,
  });

  const {
    members,
    isLoading: isMembersLoading,
    isError: isMembersError,
  } = useOrgMembers(orgId);

  const viewerUserIds = useMemo(
    () =>
      new Set(
        members.filter(m => m.memberKind === 'viewer').map(m => m.userId)
      ),
    [members]
  );

  return useMemo(() => {
    const allUsers = result.data ?? (EMPTY_USERS as OrgUserOption[]);
    const users = isMembersError
      ? allUsers
      : allUsers.filter(u => !viewerUserIds.has(u.userId));
    return {
      users,
      isLoading: result.isLoading || isMembersLoading,
      isError: result.isError,
    };
  }, [
    result.data,
    result.isLoading,
    result.isError,
    isMembersLoading,
    isMembersError,
    viewerUserIds,
  ]);
}
